import { useEffect, useReducer, useRef, useState } from 'react';
import './styles/tokens.css';
import './styles/widget.css';
import { ApiClient } from './api';
import type {
  Bookmaker,
  MatchResponse,
  QuizAnswers,
  SportSlug,
  BettingStyleSlug,
  StakeBand,
  PlatformFeatureSlug,
  Step,
} from './types';
import { Landing } from './components/Landing';
import {
  Q1Sports,
  Q2BettingStyle,
  Q3StakeSize,
  Q4Features,
  Q5Bookmakers,
} from './components/Questions';
import { Results, Settlement } from './components/Results';

interface AppState {
  step: Step;
  answers: QuizAnswers;
}

type Action =
  | { type: 'go'; step: Step }
  | { type: 'toggle_sport'; sport: SportSlug }
  | { type: 'pick_style'; style: BettingStyleSlug }
  | { type: 'pick_stake'; stake: StakeBand }
  | { type: 'toggle_feature'; feature: PlatformFeatureSlug }
  | { type: 'toggle_bookmaker'; id: number }
  | { type: 'reset' };

const initialState: AppState = {
  step: 'landing',
  answers: {
    sports: [],
    bettingStyle: null,
    stakeBand: null,
    features: [],
    existingBookmakers: [],
  },
};

function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'go':
      return { ...state, step: action.step };
    case 'toggle_sport':
      return {
        ...state,
        answers: {
          ...state.answers,
          sports: toggleArray(state.answers.sports, action.sport),
        },
      };
    case 'pick_style':
      return { ...state, answers: { ...state.answers, bettingStyle: action.style } };
    case 'pick_stake':
      return { ...state, answers: { ...state.answers, stakeBand: action.stake } };
    case 'toggle_feature':
      return {
        ...state,
        answers: {
          ...state.answers,
          features: toggleArray(state.answers.features, action.feature),
        },
      };
    case 'toggle_bookmaker':
      return {
        ...state,
        answers: {
          ...state.answers,
          existingBookmakers: toggleArray(state.answers.existingBookmakers, action.id),
        },
      };
    case 'reset':
      return initialState;
  }
}

function toggleArray<T>(arr: T[], value: T): T[] {
  return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
}

interface Props {
  apiBase: string;
  variant?: 'default' | 'sidebar';
}

export function App({ apiBase, variant = 'default' }: Props) {
  const apiRef = useRef(new ApiClient(apiBase));
  const [state, dispatch] = useReducer(reducer, initialState);
  const [bookmakers, setBookmakers] = useState<Bookmaker[]>([]);
  const [matchData, setMatchData] = useState<MatchResponse | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    apiRef.current
      .getBookmakers(ctrl.signal)
      .then(setBookmakers)
      .catch((e) => {
        if ((e as { name?: string }).name !== 'AbortError') {
          console.warn('Failed to load bookmakers', e);
        }
      });
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (state.step !== 'submitting') return;
    const ctrl = new AbortController();
    // Hold the bet-slip "settling" animation a bit longer so it feels considered.
    const minDelay = new Promise((r) => setTimeout(r, 1600));
    Promise.all([apiRef.current.match(state.answers, ctrl.signal), minDelay])
      .then(([data]) => {
        setMatchData(data);
        setMatchError(null);
        dispatch({ type: 'go', step: 'results' });
      })
      .catch((e) => {
        if ((e as { name?: string }).name === 'AbortError') return;
        setMatchError((e as Error).message ?? 'Something went wrong');
        dispatch({ type: 'go', step: 'results' });
      });
    return () => ctrl.abort();
  }, [state.step, state.answers]);

  return (
    <div className={'fb-finder' + (variant === 'sidebar' ? ' fb-finder--sidebar' : '')}>
      {renderStep(state, dispatch, bookmakers, apiRef.current, matchData, matchError)}
    </div>
  );
}

function renderStep(
  state: AppState,
  dispatch: (a: Action) => void,
  bookmakers: Bookmaker[],
  api: ApiClient,
  matchData: MatchResponse | null,
  matchError: string | null
) {
  const { step, answers } = state;

  switch (step) {
    case 'landing':
      return (
        <Landing
          bookmakerCount={bookmakers.length}
          bookmakers={bookmakers}
          onStart={() => dispatch({ type: 'go', step: 'q1' })}
        />
      );
    case 'q1':
      return (
        <Q1Sports
          answers={answers}
          onToggle={(s) => dispatch({ type: 'toggle_sport', sport: s })}
          onNext={() => dispatch({ type: 'go', step: 'q2' })}
        />
      );
    case 'q2':
      return (
        <Q2BettingStyle
          answers={answers}
          onPick={(s) => dispatch({ type: 'pick_style', style: s })}
          onBack={() => dispatch({ type: 'go', step: 'q1' })}
          onNext={() => dispatch({ type: 'go', step: 'q3' })}
        />
      );
    case 'q3':
      return (
        <Q3StakeSize
          answers={answers}
          onPick={(s) => dispatch({ type: 'pick_stake', stake: s })}
          onBack={() => dispatch({ type: 'go', step: 'q2' })}
          onNext={() => dispatch({ type: 'go', step: 'q4' })}
        />
      );
    case 'q4':
      return (
        <Q4Features
          answers={answers}
          onToggle={(f) => dispatch({ type: 'toggle_feature', feature: f })}
          onBack={() => dispatch({ type: 'go', step: 'q3' })}
          onNext={() => dispatch({ type: 'go', step: 'q5' })}
        />
      );
    case 'q5':
      return (
        <Q5Bookmakers
          answers={answers}
          bookmakers={bookmakers}
          onToggle={(id) => dispatch({ type: 'toggle_bookmaker', id })}
          onBack={() => dispatch({ type: 'go', step: 'q4' })}
          onNext={() => dispatch({ type: 'go', step: 'submitting' })}
        />
      );
    case 'submitting':
      return <Settlement answers={answers} />;
    case 'results':
      return (
        <Results
          api={api}
          data={matchData}
          loading={false}
          error={matchError}
          onRestart={() => dispatch({ type: 'reset' })}
        />
      );
  }
}
